import { afterNextRender, Component, computed, ElementRef, inject, Injector, signal, viewChild } from '@angular/core';
import {
  AppBarComponent,
  ApplicationUserService,
  CapitalizePipe,
  HelpPanelComponent,
  isHelpShortcut,
  isSearchShortcut,
  searchShortcutLabel,
  SearchBoxComponent,
  SearchHitType,
  SearchService,
} from 'shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent, DialogContainerDirective } from '@progress/kendo-angular-dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of } from 'rxjs';
import { crownbaseSearchHitTypes, defaultSearchTypes } from '@features/search/search-hit-types';
import { HELP_TOPICS, helpArticle, helpBasePath } from '@features/help/help-articles';

@Component({
  selector: 'rc-main-layout',
  imports: [
    AppBarComponent,
    DialogComponent,
    DialogContainerDirective,
    HelpPanelComponent,
    RouterOutlet,
    SearchBoxComponent,
    TranslateModule,
    CapitalizePipe,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class MainLayoutComponent {
  #applicationUserService = inject(ApplicationUserService);
  userSettings = toSignal(this.#applicationUserService.userSettings$);

  protected readonly searchHitTypes = crownbaseSearchHitTypes();
  /** The types this user may search (from the api), as far as crownbase-nxt can show them. */
  #availableSearchTypes = toSignal(inject(SearchService).getTypes().pipe(catchError(() => of(null))), { initialValue: null });
  protected readonly allSearchTypes = computed<SearchHitType[]>(() => {
    const shown = Object.keys(this.searchHitTypes) as SearchHitType[];
    const available = this.#availableSearchTypes();
    return available ? available.filter((type) => shown.includes(type)) : shown;
  });
  protected readonly searchTypes = computed(() => defaultSearchTypes(this.userSettings(), this.allSearchTypes()));

  /** The search opens in a dialog over the page, with the search icon, Ctrl/Cmd + K or Shift + F. */
  protected readonly isSearchOpen = signal(false);
  protected readonly searchShortcut = searchShortcutLabel();
  private readonly searchBox = viewChild<SearchBoxComponent>('searchBox');
  #injector = inject(Injector);

  toggleSearch() {
    if (this.isSearchOpen()) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  /** The search box gets the focus right away, so what the user types after the shortcut lands in it. */
  openSearch() {
    this.isSearchOpen.set(true);
    afterNextRender(() => this.searchBox()?.focus(), { injector: this.#injector });
  }

  closeSearch() {
    this.isSearchOpen.set(false);
  }

  /** A click next to the search, on the dark overlay, closes it. */
  onSearchDialogClick(event: MouseEvent) {
    if ((event.target as HTMLElement | null)?.classList.contains('k-overlay')) this.closeSearch();
  }

  /** The help opens at the right, with the manual of the current screen; it follows when the user navigates. */
  protected readonly isHelpOpen = signal(false);
  /** The manuals in the language the app shows (it follows a language switch at once). */
  #translate = inject(TranslateService);
  readonly #shownLanguage = toSignal(this.#translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.#translate.currentLang,
  });
  protected readonly helpBasePath = computed(() => helpBasePath(this.#shownLanguage() ?? this.userSettings()?.language));
  protected readonly helpTopics = HELP_TOPICS;
  #router = inject(Router);
  protected readonly helpArticle = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => helpArticle(event.urlAfterRedirects)),
    ),
    { initialValue: helpArticle(this.#router.url) },
  );
  private readonly helpPanel = viewChild('helpPanel', { read: ElementRef });

  toggleHelp() {
    if (this.isHelpOpen()) {
      this.closeHelp();
    } else {
      this.openHelp();
    }
  }

  openHelp() {
    this.isHelpOpen.set(true);
    afterNextRender(() => this.helpPanel()?.nativeElement.focus(), { injector: this.#injector });
  }

  closeHelp() {
    this.isHelpOpen.set(false);
  }

  /**
   * Ctrl/Cmd + K or Shift + F opens and focuses the search, ? or F1 the help. While typing, Shift + F and ? are just a
   * capital F or a question mark; Ctrl/Cmd + K still works in a text field (not in the editor) and F1 everywhere.
   */
  onDocumentKeydown(event: KeyboardEvent) {
    if (isSearchShortcut(event)) {
      event.preventDefault();
      this.openSearch();
    } else if (isHelpShortcut(event)) {
      event.preventDefault();
      this.openHelp();
    }
  }
}
